// src/pages/AboutPage.jsx
import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async"; // Added Helmet import
import NetworkIconLoader from "../components/NetworkIconLoader";
import Footer from "../components/Footer";

gsap.registerPlugin(ScrollTrigger);

// Komponen Helper untuk efek mengetik/highlight (Typewriter Reveal)
const Typewriter = ({ text, className = "", tag = "p", speed = 0.02, delay = 0 }) => {
  const el = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const chars = el.current.querySelectorAll(".char");
      gsap.to(chars, {
        opacity: 1,
        stagger: speed,
        delay: delay,
        ease: "none",
        scrollTrigger: {
          trigger: el.current,
          start: "top 85%",
          toggleActions: "play none none reverse", // Reset saat scroll ke atas agar bisa dimainkan lagi
        },
      });
    }, el);
    return () => ctx.revert();
  }, [text, speed, delay]);

  const Component = tag;
  const splitText = text.split("").map((char, i) => (
    <span key={i} className="char opacity-25 transition-opacity duration-300">
      {char}
    </span>
  ));

  return (
    <Component ref={el} className={className}>
      {splitText}
    </Component>
  );
};

const AboutPage = () => {
  const rootRef = useRef(null);
  const { t } = useTranslation();

  // Animasi Background Orbs
  useEffect(() => {
    const ctx = gsap.context(() => {
      // Orbs biru parallax + floating
      gsap.utils.toArray(".about-orb").forEach((orb) => {
        const speed = parseFloat(orb.dataset.speed || "1");

        // parallax scroll
        gsap.to(orb, {
          y: () => 100 * speed,
          ease: "none",
          scrollTrigger: {
            trigger: rootRef.current,
            start: "top top",
            end: "bottom bottom",
            scrub: true,
          },
        });

        // floating loop
        gsap.to(orb, {
          y: "+=20",
          x: "+=15",
          duration: 4 + speed,
          repeat: -1,
          yoyo: true,
          ease: "sine.inOut",
        });
      });
    }, rootRef);

    return () => ctx.revert();
  }, []);

  return (
    <div
      ref={rootRef}
      className="relative min-h-screen bg-slate-50 text-slate-900 overflow-x-hidden font-sans"
    >
      <Helmet>
        <title>{t('aboutPage.heroTitle') ? `${t('aboutPage.heroTitle')} - Neo AI` : 'About Us - Neo AI'}</title>
        <meta name="description" content={t('aboutPage.heroDesc') || 'Learn more about Neo AI, our vision, mission, and values.'} />
      </Helmet>

      {/* BACKGROUND DEKORASI */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        <div className="absolute inset-0 opacity-60">
          <div className="w-full h-full bg-[radial-gradient(circle_at_top,_rgba(224,242,254,0.5),_transparent_70%)]" />
        </div>
        {/* Grid dots halus */}
        <div className="absolute inset-0 opacity-[0.4]" style={{ backgroundImage: 'radial-gradient(#cbd5e1 1px, transparent 1px)', backgroundSize: '32px 32px' }}></div>

        {/* Orbs */}
        <div className="about-orb absolute -top-24 -left-16 w-80 h-80 rounded-full bg-blue-200/40 blur-3xl" data-speed="0.4" />
        <div className="about-orb absolute top-1/2 -right-20 w-96 h-96 rounded-full bg-sky-200/40 blur-3xl" data-speed="0.6" />
      </div>

      {/* CONTENT SINGLE COLUMN */}
      <div className="relative max-w-3xl mx-auto px-6 py-20 sm:py-28 flex flex-col gap-24">

        {/* HERO SECTION */}
        <section className="text-left">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight text-slate-900 mb-8 leading-tight">
            <span className="block text-sky-600 mb-2 text-xl sm:text-2xl font-semibold tracking-normal uppercase">{t('aboutPage.heroLabel')}</span>
            {t('aboutPage.heroTitle')}
          </h1>
          <div className="pl-6 border-l-4 border-sky-500">
            <Typewriter
              tag="p"
              className="text-lg sm:text-xl text-slate-600 leading-relaxed font-medium"
              text={t('aboutPage.heroDesc')}
              speed={0.01}
            />
          </div>
        </section>

        {/* VISI */}
        <section>
          <h2 className="text-5xl font-extrabold text-[#164361] mb-8 uppercase tracking-tight">{t('aboutPage.visionLabel')}</h2>
          <Typewriter
            tag="h3"
            className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-800 leading-snug"
            text={t('aboutPage.visionText')}
            speed={0.015}
          />
        </section>

        {/* MISI */}
        <section>
          <h2 className="text-5xl font-extrabold text-[#164361] mb-8 uppercase tracking-tight">{t('aboutPage.missionLabel')}</h2>
          <div className="flex flex-col gap-6">
            <div>
              <span className="text-sky-500 font-bold text-lg block mb-2">{t('aboutPage.mission1Title')}</span>
              <Typewriter
                className="text-lg text-slate-700 leading-relaxed"
                text={t('aboutPage.mission1Text')}
              />
            </div>
            <div>
              <span className="text-sky-500 font-bold text-lg block mb-2">{t('aboutPage.mission2Title')}</span>
              <Typewriter
                className="text-lg text-slate-700 leading-relaxed"
                text={t('aboutPage.mission2Text')}
                delay={2}
              />
            </div>
            <div>
              <span className="text-sky-500 font-bold text-lg block mb-2">{t('aboutPage.mission3Title')}</span>
              <Typewriter
                className="text-lg text-slate-700 leading-relaxed"
                text={t('aboutPage.mission3Text')}
                delay={4}
              />
            </div>
          </div>
        </section>

        {/* VALUES */}
        <section>
          <h2 className="text-5xl font-extrabold text-[#164361] mb-8 uppercase tracking-tight">{t('aboutPage.valuesLabel')}</h2>
          <div className="flex flex-col gap-8">
            <div>
              <h3 className="text-2xl font-bold text-slate-900 mb-2 flex items-center gap-3">
                {t('aboutPage.value1Title')}
              </h3>
              <Typewriter
                className="text-lg text-slate-600"
                text={t('aboutPage.value1Text')}
              />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-slate-900 mb-2 flex items-center gap-3">
                {t('aboutPage.value2Title')}
              </h3>
              <Typewriter
                className="text-lg text-slate-600"
                text={t('aboutPage.value2Text')}
                delay={2}
              />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-slate-900 mb-2 flex items-center gap-3">
                {t('aboutPage.value3Title')}
              </h3>
              <Typewriter
                className="text-lg text-slate-600"
                text={t('aboutPage.value3Text')}
                delay={4}
              />
            </div>
          </div>
        </section>

      </div>

      <Footer />

      <NetworkIconLoader />
    </div>
  );
};

export default AboutPage;
